import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/lib/auth-context";
import { MODULES, itensDoPublico } from "@/lib/modules";
import { useBadgesMercado } from "@/hooks/use-mercado";
import { useSidebar } from "@/components/ui/sidebar";
import { MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * A casca das telas do PRODUTOR no /mercado.
 *
 * - Largura cheia (nada de bloco centralizado e estreito — o header é o mesmo
 *   da tela de Vendas do Cacho).
 * - No celular, tabbar FIXA embaixo com os cinco itens (Início · Cotações ·
 *   Ofertas · Corretores · A receber), com badge de contagem. Os itens saem do
 *   registry de módulos, para que o menu lateral e a tabbar nunca divirjam.
 * - Org de COMPRADOR que cair aqui volta para a home do Mercado: as rotas do
 *   produtor respondem 403 para ela, e uma tela de erro não é resposta.
 */
export function ProdutorShell({ children, className }: { children: React.ReactNode; className?: string }) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const ehCorretor = user?.orgTipo === "COMPRADOR";

  useEffect(() => {
    if (ehCorretor) setLocation("/mercado", { replace: true } as any);
  }, [ehCorretor]);

  if (ehCorretor) return null;

  return (
    <>
      {/* `.pagina-container` marca a largura que o cabeçalho mede (container
          query). A tabbar fica FORA dele: `container-type` cria containment, e
          um `position: fixed` lá dentro passaria a se posicionar pelo container
          em vez de pela janela. */}
      <div className={cn("pagina-container p-4 md:p-6 space-y-4 pb-28 md:pb-6", className)}>
        {children}
      </div>
      <TabbarMercado orgTipo="PRODUTOR" />
    </>
  );
}

/**
 * A casca das telas do COMPRADOR — a mesma tabbar, com os itens dele.
 *
 * Nasceu no PR 3, quando o comprador passou a ter DUAS telas de lista (Praça e
 * Romaneios) além da home: com uma só, o menu lateral bastava; com três, quem
 * está no celular precisa trocar sem abrir gaveta. Os itens saem do MESMO
 * registry, e é por isso que o menu e a tabbar não podem divergir.
 */
export function CompradorShell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <>
      <div className={cn("pagina-container p-4 md:p-6 pb-28 md:pb-6", className)}>
        {children}
      </div>
      <TabbarMercado orgTipo="COMPRADOR" />
    </>
  );
}

/** Tailwind não vê classe montada em runtime — o mapa é o que mantém o purge honesto. */
const COLUNAS: Record<number, string> = {
  1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4", 5: "grid-cols-5",
};

/**
 * Quantos itens cabem na tabbar antes de "mais".
 *
 * Cinco é o teto físico do polegar num 390px: com seis, cada alvo cai abaixo de
 * 65px e o rótulo trunca. Quando a visão tem mais que isso (o COMPRADOR passou a
 * ter seis no C4 e o CORRETOR, sete no C8b), os quatro primeiros ficam e o resto vai para o "mais", que
 * abre a MESMA gaveta do menu lateral — não uma segunda navegação a manter.
 */
const MAX_TABBAR = 5;

function TabbarMercado({ orgTipo }: { orgTipo: "PRODUTOR" | "COMPRADOR" }) {
  const [location, setLocation] = useLocation();
  const badges = useBadgesMercado();
  const { setOpenMobile } = useSidebar();
  const { user } = useAuth();
  // O MESMO filtro do menu lateral, agora literalmente a mesma função — era o
  // que este comentário prometia e duas cópias não entregavam.
  const todos = itensDoPublico(MODULES.find(m => m.id === "mercado")?.nav ?? [], {
    orgTipo,
    perfilComprador: user?.perfilComprador,
  });
  // A ORDEM do registry é a prioridade: o que vem primeiro é o que o usuário
  // abre mais. Cortar pelo fim mantém menu lateral e tabbar na mesma ordem.
  const excedente = todos.length > MAX_TABBAR;
  const itens = excedente ? todos.slice(0, MAX_TABBAR - 1) : todos;
  const colunas = itens.length + (excedente ? 1 : 0);

  return (
    <nav
      className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
      aria-label="Mercado"
      data-testid="tabbar-mercado"
    >
      <ul className={cn("grid", COLUNAS[colunas] ?? "grid-cols-3")}>
        {itens.map(item => {
          const ativo = location === item.path || (item.path !== "/mercado" && location.indexOf(item.path + "/") === 0);
          const badge = item.badge ? badges[item.badge] : 0;
          return (
            <li key={item.path}>
              <button
                type="button"
                onClick={() => setLocation(item.path)}
                className={cn(
                  "w-full h-14 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium relative",
                  ativo ? "text-primary" : "text-muted-foreground",
                )}
                aria-current={ativo ? "page" : undefined}
                data-testid={`tab-${item.path.replace(/\//g, "-").slice(1)}`}
              >
                <item.icon className="h-5 w-5" />
                <span className="truncate max-w-full px-1">{item.label}</span>
                {badge > 0 && (
                  <span className="absolute top-1.5 right-1/2 translate-x-4 min-w-4 h-4 px-1 rounded-full bg-atencao text-[10px] font-bold text-white flex items-center justify-center">
                    {badge}
                  </span>
                )}
              </button>
            </li>
          );
        })}
        {excedente && (
          <li>
            <button
              type="button"
              onClick={() => setOpenMobile(true)}
              className="w-full h-14 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-muted-foreground"
              data-testid="tab-mais"
            >
              <MoreHorizontal className="h-5 w-5" />
              <span>mais</span>
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
}
